import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Copy, Check, AlertTriangle, AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

/**
 * App-level replacement for window.alert / window.confirm and rich-detail error toasts.
 *
 * Why it exists:
 *   - The browser's native alert/confirm dialogs are opaque to support — they can't
 *     carry stack traces or be copied with one click. When something fails in a LinkedIn
 *     extraction or campaign launch, we want the full server response on the user's
 *     clipboard so it can be pasted to Claude for debugging.
 *   - Native popups also block everything including hot-reload and keyboard shortcuts.
 *
 * Usage:
 *   const msg = useAppMessage();
 *   await msg.confirm('Delete campaign?', 'This cannot be undone.');
 *   msg.error('Launch failed', err.message, err.response?.data);
 */

const Ctx = createContext(null);

function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title="Copy to clipboard"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(text || '');
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch {}
      }}
      className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-gray-200 hover:bg-gray-100 ${className}`}
    >
      {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function formatDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  try { return JSON.stringify(detail, null, 2); } catch { return String(detail); }
}

export function AppMessageProvider({ children }) {
  const [confirmState, setConfirmState] = useState(null);   // {title, body, resolver, confirmLabel, destructive}
  const resolverRef = useRef(null);

  const confirm = useCallback((title, body, { confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive = false } = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfirmState({ title, body, confirmLabel, cancelLabel, destructive });
    });
  }, []);

  const closeConfirm = (result) => {
    if (resolverRef.current) resolverRef.current(result);
    resolverRef.current = null;
    setConfirmState(null);
  };

  // Rich error toast with copy button for full debug detail.
  const error = useCallback((title, message = '', detail = null, { durationMs = 12000 } = {}) => {
    const fullText = [title, message, formatDetail(detail)].filter(Boolean).join('\n\n');
    toast.custom(
      (id) => (
        <div className="w-[420px] rounded-lg border border-rose-300 bg-white shadow-lg">
          <div className="flex items-start gap-3 px-4 py-3 border-b border-rose-100">
            <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-rose-900">{title}</p>
              {message && <p className="text-xs text-rose-800 mt-1 break-words whitespace-pre-wrap">{message}</p>}
            </div>
            <button onClick={() => toast.dismiss(id)} className="text-rose-400 hover:text-rose-700 shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          {detail && (
            <div className="px-4 py-2.5 bg-gray-50 border-b border-rose-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Server detail</span>
                <CopyButton text={formatDetail(detail)} />
              </div>
              <pre className="text-[11px] font-mono text-gray-700 bg-white border border-gray-100 rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
                {formatDetail(detail)}
              </pre>
            </div>
          )}
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">Share with Claude to debug</span>
            <CopyButton text={fullText} className="bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100" />
          </div>
        </div>
      ),
      { duration: durationMs },
    );
  }, []);

  const success = useCallback((title, message = '') => {
    toast.custom((id) => (
      <div className="w-[360px] rounded-lg border border-emerald-200 bg-white shadow-lg flex items-start gap-3 px-4 py-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900">{title}</p>
          {message && <p className="text-xs text-emerald-800 mt-0.5 break-words">{message}</p>}
        </div>
        <button onClick={() => toast.dismiss(id)} className="text-emerald-400 hover:text-emerald-700 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    ), { duration: 4000 });
  }, []);

  const info = useCallback((title, message = '', detail = null) => {
    const full = [title, message, formatDetail(detail)].filter(Boolean).join('\n\n');
    toast.custom((id) => (
      <div className="w-[400px] rounded-lg border border-blue-200 bg-white shadow-lg">
        <div className="flex items-start gap-3 px-4 py-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-blue-900">{title}</p>
            {message && <p className="text-xs text-blue-800 mt-1 break-words whitespace-pre-wrap">{message}</p>}
          </div>
          <CopyButton text={full} />
          <button onClick={() => toast.dismiss(id)} className="text-blue-400 hover:text-blue-700 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        {detail && (
          <pre className="text-[11px] font-mono text-gray-700 bg-gray-50 border-t border-blue-100 p-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
            {formatDetail(detail)}
          </pre>
        )}
      </div>
    ), { duration: 8000 });
  }, []);

  const warn = useCallback((title, message = '', detail = null) => {
    const full = [title, message, formatDetail(detail)].filter(Boolean).join('\n\n');
    toast.custom((id) => (
      <div className="w-[400px] rounded-lg border border-amber-300 bg-white shadow-lg">
        <div className="flex items-start gap-3 px-4 py-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">{title}</p>
            {message && <p className="text-xs text-amber-800 mt-1 break-words whitespace-pre-wrap">{message}</p>}
          </div>
          <CopyButton text={full} />
          <button onClick={() => toast.dismiss(id)} className="text-amber-500 hover:text-amber-700 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        {detail && (
          <pre className="text-[11px] font-mono text-gray-700 bg-gray-50 border-t border-amber-100 p-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
            {formatDetail(detail)}
          </pre>
        )}
      </div>
    ), { duration: 9000 });
  }, []);

  return (
    <Ctx.Provider value={{ confirm, error, success, info, warn }}>
      {children}
      <AlertDialog open={!!confirmState} onOpenChange={(open) => { if (!open) closeConfirm(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-wrap">
              {confirmState?.body}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => closeConfirm(false)}>
              {confirmState?.cancelLabel || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => closeConfirm(true)}
              className={confirmState?.destructive ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
            >
              {confirmState?.confirmLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}

export function useAppMessage() {
  const v = useContext(Ctx);
  if (!v) {
    // Fallback when provider is not mounted — still avoid window.alert/confirm
    return {
      confirm: async () => true,
      error: (title, message, detail) => toast.error(`${title}${message ? ': ' + message : ''}`),
      success: (title) => toast.success(title),
      info: (title) => toast(title),
      warn: (title) => toast.warning(title),
    };
  }
  return v;
}

export default useAppMessage;
